<script setup lang="ts">
import { Cake } from 'lucide-vue-next'
import type { PaletteHue } from '@/design-system/components'

/** Prepared birthday row (built in Today.vue from upcoming birthdays). */
export interface BirthdayRow {
  id: string
  initials: string
  hue: PaletteHue
  name: string
  age: number | null
  className: string
  /** e.g. "WED 6" — omitted when it's today (the TODAY pill shows instead). */
  dayLabel: string
  isToday: boolean
  photoUrl?: string | null
}

defineProps<{ rows: BirthdayRow[] }>()
const emit = defineEmits<{ openStudent: [id: string] }>()
</script>

<template>
  <div class="bday">
    <div class="bday__head">
      <span class="bday__tile"><Cake :size="14" /></span>
      <span class="bday__eyebrow">Birthdays this week</span>
      <span class="bday__count">{{ rows.length }}</span>
    </div>
    <div class="bday__body">
      <p v-if="!rows.length" class="bday__empty">No birthdays this week.</p>
      <button
        v-for="b in rows"
        :key="b.id"
        type="button"
        class="bday__row"
        @click="emit('openStudent', b.id)"
      >
        <span v-if="b.photoUrl" class="bday__avatar">
          <img :src="b.photoUrl" alt="" />
        </span>
        <span v-else class="bday__avatar bday__avatar--mono" :style="{ background: b.hue.soft, color: b.hue.ink }">
          {{ b.initials }}
        </span>
        <span class="bday__text">
          <span class="bday__name">{{ b.name }}</span>
          <span class="bday__meta">
            <template v-if="b.age != null">Turns {{ b.age }} · </template>{{ b.className }}
          </span>
        </span>
        <span v-if="b.isToday" class="bday__today">TODAY</span>
        <span v-else class="bday__day">{{ b.dayLabel }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.bday {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  font-family: var(--font-sans);
}
.bday__head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 14px 16px 10px;
}
.bday__tile {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--coral-soft);
  color: var(--coral);
}
.bday__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--coral);
}
.bday__count {
  margin-left: auto;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: var(--coral);
}
.bday__body {
  padding: 0 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.bday__empty {
  margin: 4px 0 4px;
  font-size: 13px;
  color: var(--ink-400);
}
.bday__row {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
}
.bday__avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bday__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bday__avatar--mono {
  font-size: 11px;
  font-weight: 600;
}
.bday__text {
  flex: 1;
  min-width: 0;
}
.bday__name {
  display: block;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bday__meta {
  display: block;
  font-size: 12px;
  color: var(--ink-500);
}
.bday__today {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--coral);
  background: var(--coral-soft);
  padding: 3px 8px;
  border-radius: var(--radius-pill);
}
.bday__day {
  flex-shrink: 0;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  letter-spacing: 0.4px;
  color: var(--ink-500);
}
</style>
